import { defineMessages, useIntl } from 'react-intl';
import { Loading, Modal, Tooltip } from '@carbon/react';
import { Information } from '@carbon/icons-react';
import moment from 'moment';
import { useEffect, useState } from 'react';

import styles from './EventHandler.module.scss';

import sharedMessages from 'Components/utils/sharedMessages';
import EventTitleInput from 'Screens/Dashboard/Events/EventHandler/inputs/EventTitleInput/EventTitleInput';
import EventPlaceInput from 'Screens/Dashboard/Events/EventHandler/inputs/EventPlaceInput/EventPlaceInput';
import EventRecurrence from 'Screens/Dashboard/Events/EventHandler/inputs/EventRecurrence/EventRecurrence';
import EventDescriptionInput from 'Screens/Dashboard/Events/EventHandler/inputs/EventDescriptionInput/EventDescriptionInput';
import EventPeriod from 'Screens/Dashboard/Events/EventHandler/inputs/EventPeriod/EventPeriod';
import EventTypePicker from 'Screens/Dashboard/Events/EventHandler/inputs/EventTypePicker/EventTypePicker';
import EventPointsInput from 'Screens/Dashboard/Events/EventHandler/inputs/EventPointsInput/EventPointsInput';
import EventGlobalScopeInput from 'Screens/Dashboard/Events/EventHandler/inputs/EventGlobalScopeInput/EventGlobalScopeInput';
import EventColorInput from 'Screens/Dashboard/Events/EventHandler/inputs/EventColorInput/EventColorInput';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import { useGetCategoriesByEntityQuery } from 'api/categoryEndpoints';
import { useEventContext } from 'Screens/Dashboard/Events/EventHandler/EntityEventContext';
import AttendeesDataTable from 'Screens/Dashboard/Events/EventHandler/inputs/AttendeesDataTable/AttendeesDataTable';
import useGetUserPermission from 'hooks/useGetUserPermission';
import EventOptionalAttendeePointsInput from './inputs/EventOptionalAttendeePointsInput/EventOptionalAttendeePointsInput';
import EventVisibleForGuestsToggle from 'Screens/Dashboard/Events/EventHandler/inputs/EventVisibilityToggle/EventVisibilityToggle';
import { HowLongMode, RecurrenceType } from 'api/dtos/event';
import useCurrentEntityUsesPoints from 'hooks/useCurrentEntityUsesPoints';
import EventAbsencePenaltyPointsInput from './inputs/EventAbsencePenaltyPointsInput/EventAbsencePenaltyPointsInput';
import EventQrConfirmationWindowInput from './inputs/EventQrConfirmationWindowInput/EventQrConfirmationWindowInput';
import EventEntityPicker from './inputs/EventEntityPicker/EventEntityPicker';
import useGetUser from 'hooks/useGetUser';
import EventReminderInput from './inputs/EventReminderInput/EventReminderInput';
import { getOneYearRecurrenceEndDate } from './inputs/EventRecurrence/recurrenceUtils';

type Props = {
    open: boolean;
    onCloseRequest: () => void;
    modalHeading: string;
    primaryButtonText: string;
    handleSubmit: () => void;
    isLoading?: boolean;
    onDeleteRequest?: () => void;
}

type SecondaryButton = {
    buttonText: string;
    onClick?: () => void;
    kind?: 'danger' | 'secondary';
}

const recurrenceLabels = ['Brak', 'Codziennie', 'Co tydzień', 'Co miesiąc', 'Co rok'];

const getValidExcludedDays = (excludedDays: number[] | undefined) => (
    [...new Set(excludedDays ?? [])]
        .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)
        .sort((a, b) => a - b)
);

const formatEventTimeRange = (dateFrom: string, dateTo: string, allDay: boolean, allDayLabel: string) => {
    if (!dateFrom || !dateTo) {
        return '-';
    }

    if (allDay) {
        return allDayLabel;
    }

    return `${moment(dateFrom).format('HH:mm')} - ${moment(dateTo).format('HH:mm')}`;
};

const EventHandler = ({
    open,
    onCloseRequest,
    modalHeading,
    primaryButtonText,
    handleSubmit,
    isLoading,
    onDeleteRequest,
}: Props) => {
    const intl = useIntl();
    const currentEntityId = useGetCurrentEntityId();
    const { values } = useEventContext();
    const { isAdmin, isModerator, isOwner, isGuest } = useGetUserPermission();
    const currentEntityUsesPoints = useCurrentEntityUsesPoints();
    const user = useGetUser();
    const selectedEntityId = values.entityId ?? currentEntityId;
    const usesPoints = user?.entities.find((entity) => entity.id === selectedEntityId)?.usesPoints ?? currentEntityUsesPoints;
    const isEdit = Boolean(values.id);
    const [showCategoryError, setShowCategoryError] = useState(false);
    const isMissingCategory = values.categoryIds.length === 0;
    const isGlobalEvent = values.kind === 'Global' || values.isGlobal;
    const isGlobalSource = Boolean(values.isGlobalSource);
    const isGlobalLocalContext = isEdit && isGlobalEvent && !isGlobalSource;
    const shouldBlockGlobalSourceActions = isGlobalEvent && !isGlobalLocalContext;
    const canDelete = isEdit && (isAdmin || isModerator || isOwner);
    const isMissingGlobalTargets = !isGlobalLocalContext &&
        isGlobalEvent &&
        values.scope === 'GlobalSelectedChildren' &&
        (values.targetEntityIds ?? []).length === 0;
    const hasInvalidAbsencePenalty = usesPoints
        && values.absencePenaltyPoints !== null
        && values.absencePenaltyPoints !== undefined
        && values.absencePenaltyPoints >= 0;
    const hasInvalidQrWindow = usesPoints && (!values.qrConfirmationWindowMinutes || values.qrConfirmationWindowMinutes <= 0);
    const hasInvalidReminder = values.attendeeReminderMinutesBefore !== null &&
        values.attendeeReminderMinutesBefore !== undefined &&
        (values.attendeeReminderMinutesBefore < 0 || values.attendeeReminderMinutesBefore > 43200);
    const recurrenceUntilDate = values.howLong?.untilDate;
    const hasConfigurableRecurrenceEnd = values.recurrence.type !== RecurrenceType.None
        && values.recurrence.type !== RecurrenceType.Yearly;
    const hasInvalidUntilDate = hasConfigurableRecurrenceEnd
        && values.howLong?.mode === HowLongMode.UntilDate
        && (!recurrenceUntilDate
            || moment(recurrenceUntilDate).isBefore(moment(values.dateFrom), 'day')
            || moment(recurrenceUntilDate).isAfter(moment(getOneYearRecurrenceEndDate()), 'day'));
    const hasInvalidRepeatTimes = hasConfigurableRecurrenceEnd
        && values.howLong?.mode === HowLongMode.ForXTimes
        && (!values.howLong.times || values.howLong.times <= 0);
    const hasMissingTime = !values.allDay && (!values.timeFrom.length || !values.timeTo.length);
    const effectiveEndDate = values.isMultiDay ? values.dateTo : values.dateFrom;
    const hasInvalidPeriod = (() => {
        if (!values.dateFrom || !effectiveEndDate) return false;

        if (values.allDay) {
            return moment(effectiveEndDate).isBefore(moment(values.dateFrom), 'day');
        }

        if (hasMissingTime) return false;

        const start = moment(
            `${moment(values.dateFrom).format('YYYY-MM-DD')} ${values.timeFrom}`,
            'YYYY-MM-DD HH:mm',
            true,
        );
        const end = moment(
            `${moment(effectiveEndDate).format('YYYY-MM-DD')} ${values.timeTo}`,
            'YYYY-MM-DD HH:mm',
            true,
        );

        return !start.isValid() || !end.isValid() || !end.isAfter(start);
    })();
    const excludedDays = getValidExcludedDays(values.recurrence.daily?.excludedDays);
    const hasAllDailyDaysExcluded = values.recurrence.type === RecurrenceType.Daily
        && excludedDays.length === 7;
    const isDisabled = !(values.title.trim().length
        && values.dateFrom.length
        && values.dateTo.length) ||
        hasMissingTime ||
        hasInvalidPeriod ||
        (!isGlobalLocalContext && isMissingCategory) ||
        isMissingGlobalTargets ||
        hasInvalidAbsencePenalty ||
        hasInvalidQrWindow ||
        hasInvalidReminder ||
        hasInvalidUntilDate ||
        hasInvalidRepeatTimes ||
        hasAllDailyDaysExcluded;

    const { isFetching } = useGetCategoriesByEntityQuery(selectedEntityId ?? '', {
        skip: !selectedEntityId || !open,
    });

    useEffect(() => {
        if (!open) {
            setShowCategoryError(false);
        }
    }, [open]);

    const handleFormSubmit = () => {
        if (isGuest && isEdit) {
            onCloseRequest();
            return;
        }

        if (!isGlobalLocalContext && isMissingCategory) {
            setShowCategoryError(true);
            window.setTimeout(() => {
                document.getElementById('event-type-picker')?.focus();
            }, 0);
            return;
        }

        handleSubmit();
    };

    const getSecondaryButtons = (): SecondaryButton[] | undefined => {
        if (!isEdit) return undefined;

        const closeButton = {
            buttonText: intl.formatMessage(sharedMessages.close),
            onClick: onCloseRequest,
        };

        if (isGuest) return [closeButton];

        if (!canDelete) return [closeButton];

        return [{
            buttonText: intl.formatMessage(sharedMessages.close),
            onClick: onCloseRequest,
        }, {
            buttonText: intl.formatMessage(messages.deleteEvent),
            onClick: onDeleteRequest,
            kind: 'danger',
        }];
    };

    const secondaryButtons = getSecondaryButtons();
    const timeRangeText = formatEventTimeRange(
        values.dateFrom,
        values.dateTo,
        values.allDay,
        intl.formatMessage(messages.allDay),
    );
    const excludedDaysText = excludedDays
        .map((day) => intl.formatMessage(excludedDayMessageList[day]))
        .join(', ');
    const yearlyRecurrenceEndDate = values.recurrence.type === RecurrenceType.Yearly
        && values.recurrenceEndDate
        ? moment(values.recurrenceEndDate).format('LL')
        : undefined;

    const recurrenceDetails = (
        <>
            <div>{recurrenceLabels[values.recurrence.type]}</div>
            {values.recurrence.type === RecurrenceType.Daily && excludedDaysText && (
                <div>
                    {intl.formatMessage(messages.dailyExcludedDays, { days: excludedDaysText })}
                </div>
            )}
            {yearlyRecurrenceEndDate && (
                <div>
                    {intl.formatMessage(messages.yearlyRecurrenceEndDate, { date: yearlyRecurrenceEndDate })}
                </div>
            )}
        </>
    );

    return (
        <Modal
            open={open}
            onRequestClose={onCloseRequest}
            onRequestSubmit={handleFormSubmit}
            className={isEdit ? styles.modal : undefined}
            modalHeading={modalHeading}
            primaryButtonDisabled={(isGuest && isEdit) ? false : ((!isAdmin && !isModerator && !isOwner) || isLoading || isFetching || isDisabled)}
            closeButtonLabel={intl.formatMessage(sharedMessages.close)}
            primaryButtonText={isGuest && isEdit ? intl.formatMessage(sharedMessages.close) : primaryButtonText}
            secondaryButtons={secondaryButtons}
            secondaryButtonText={intl.formatMessage(sharedMessages.close)}
        >
            {(isFetching || isLoading) && <Loading />}
            {isGuest && isEdit ? (
                <>
                    <div className={styles.inputContainer}>
                        <strong>{values.title || '-'}</strong>
                    </div>
                    <div className={styles.inputContainer}>
                        <div><strong>{intl.formatMessage(messages.date)}</strong></div>
                        <div>{values.dateFrom ? moment(values.dateFrom).format('LL') : '-'}</div>
                        <div>{timeRangeText}</div>
                    </div>
                    <div className={styles.inputContainer}>
                        <div><strong>{intl.formatMessage(messages.place)}</strong></div>
                        <div>{values.eventPlace || '-'}</div>
                    </div>
                    {values.recurrence.type !== RecurrenceType.None && (
                        <div className={styles.inputContainer}>
                            <div><strong>{intl.formatMessage(messages.recurrence)}</strong></div>
                            {recurrenceDetails}
                        </div>
                    )}
                </>
            ) : (
                <>
                    {isGlobalEvent && (
                        <div className={styles.globalInfo}>
                            <span>{intl.formatMessage(messages.globalEvent)}</span>
                            <Tooltip
                                label={intl.formatMessage(isGlobalLocalContext
                                    ? messages.globalSubgroupTooltip
                                    : messages.globalSourceTooltip)}
                                align="bottom"
                                className={styles.tooltip}
                            >
                                <Information size={16} />
                            </Tooltip>
                        </div>
                    )}
                    <div className={styles.inputContainer}>
                        <EventEntityPicker />
                    </div>
                    <EventTitleInput
                        disabled={isGlobalLocalContext}
                    />
                    {!isGlobalLocalContext && (
                        <div className={styles.inputContainer}>
                            <EventGlobalScopeInput />
                        </div>
                    )}
                    {usesPoints && !shouldBlockGlobalSourceActions && (
                        <div className={styles.fieldsGrid}>
                            <EventPointsInput />
                            <EventOptionalAttendeePointsInput />
                            <EventAbsencePenaltyPointsInput />
                            <EventQrConfirmationWindowInput />
                        </div>
                    )}
                    {!isGlobalLocalContext && (
                        <>
                            <div className={styles.inputContainer}>
                                <EventColorInput />
                            </div>
                            <div className={styles.inputContainer}>
                                <EventVisibleForGuestsToggle />
                            </div>
                            <div className={styles.inputContainer}>
                                <EventPeriod />
                            </div>
                            <div className={styles.inputContainer}>
                                <EventReminderInput />
                            </div>
                            <div className={styles.inputContainer}>
                                <EventRecurrence />
                            </div>
                            {isEdit && shouldBlockGlobalSourceActions && (
                                <div className={styles.scheduleWarning}>
                                    {intl.formatMessage(messages.globalScheduleWarning)}
                                </div>
                            )}
                            <div className={styles.inputContainer}>
                                <EventTypePicker showError={showCategoryError} />
                            </div>
                        </>
                    )}
                    {isGlobalLocalContext && (
                        <>
                            <div className={styles.inputContainer}>
                                <div><strong>{intl.formatMessage(messages.date)}</strong></div>
                                <div>{values.dateFrom ? moment(values.dateFrom).format('LL') : '-'}</div>
                                <div>{timeRangeText}</div>
                            </div>
                            <div className={styles.inputContainer}>
                                <div><strong>{intl.formatMessage(messages.place)}</strong></div>
                                <div>{values.eventPlace || '-'}</div>
                            </div>
                            <div className={styles.inputContainer}>
                                <EventTypePicker readonly />
                            </div>
                            {values.recurrence.type !== RecurrenceType.None && (
                                <div className={styles.inputContainer}>
                                    <div><strong>{intl.formatMessage(messages.recurrence)}</strong></div>
                                    {recurrenceDetails}
                                </div>
                            )}
                        </>
                    )}
                    {!shouldBlockGlobalSourceActions && (
                        <div className={styles.inputContainer}>
                            <AttendeesDataTable skip={!open} />
                        </div>
                    )}
                    {shouldBlockGlobalSourceActions && (
                        <div className={styles.inputContainer}>
                            <div className={styles.disabledAttendeesHeader}>
                                <span>{intl.formatMessage(messages.attendees)}</span>
                                <Tooltip
                                    label={intl.formatMessage(messages.globalAttendeesTooltip)}
                                    align="right-end"
                                >
                                    <Information size={16} />
                                </Tooltip>
                            </div>
                        </div>
                    )}
                    {!isGlobalLocalContext && (
                        <>
                            <div className={styles.inputContainer}>
                                <EventPlaceInput />
                            </div>
                            <div className={styles.inputContainer}>
                                <EventDescriptionInput />
                            </div>
                        </>
                    )}
                </>
            )}
        </Modal>
    );
};

const messages = defineMessages({
    date: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.date',
        defaultMessage: 'Data',
    },
    place: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.place',
        defaultMessage: 'Miejsce',
    },
    recurrence: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.recurrence',
        defaultMessage: 'Powtarzanie',
    },
    dailyExcludedDays: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.dailyExcludedDays',
        defaultMessage: 'Nie odbywa się w: {days}',
    },
    yearlyRecurrenceEndDate: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.yearlyRecurrenceEndDate',
        defaultMessage: 'Wystąpienia wygenerowane do: {date}',
    },
    declineEvent: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.declineEvent',
        defaultMessage: 'Wypisz się z wydarzenia',
    },
    selfAddEvent: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.selfAddEvent',
        defaultMessage: 'Zapisz się na wydarzenie',
    },
    attendees: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.attendees',
        defaultMessage: 'Uczestnicy',
    },
    globalAttendeesTooltip: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.globalAttendeesTooltip',
        defaultMessage: 'Uczestników wydarzenia globalnego można ustawić wyłącznie z poziomu podgrupy.',
    },
    globalEvent: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.globalEvent',
        defaultMessage: 'Wydarzenie globalne',
    },
    globalSourceTooltip: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.globalSourceTooltip',
        defaultMessage: 'Na poziomie parafii nie można zarządzać uczestnikami, samodzielnym zapisem, rolami uczestników ani punktami w wydarzeniu globalnym. Te działania wykonaj w podgrupie.',
    },
    globalSubgroupTooltip: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.globalSubgroupTooltip',
        defaultMessage: 'W podgrupie nie można zmieniać tytułu, opisu, miejsca, dat, powtarzania, koloru, księży ani zakresu podgrup. Te działania wykonaj na poziomie parafii.',
    },
    globalScheduleWarning: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.globalScheduleWarning',
        defaultMessage: 'Zmiana daty, czasu lub powtarzania wydarzenia globalnego wpłynie na podgrupy. Może przywrócić lokalnie usunięte wystąpienia albo nadpisać lokalne zmiany harmonogramu dla tego wydarzenia.',
    },
    deleteEvent: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.deleteEvent',
        defaultMessage: 'Usuń to wydarzenie',
    },
    allDay: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.allDay',
        defaultMessage: 'Całodniowe',
    },
});

const excludedDayMessages = defineMessages({
    monday: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.excludedMonday',
        defaultMessage: 'poniedziałki',
    },
    tuesday: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.excludedTuesday',
        defaultMessage: 'wtorki',
    },
    wednesday: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.excludedWednesday',
        defaultMessage: 'środy',
    },
    thursday: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.excludedThursday',
        defaultMessage: 'czwartki',
    },
    friday: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.excludedFriday',
        defaultMessage: 'piątki',
    },
    saturday: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.excludedSaturday',
        defaultMessage: 'soboty',
    },
    sunday: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.excludedSunday',
        defaultMessage: 'niedziele',
    },
});

const excludedDayMessageList = [
    excludedDayMessages.monday,
    excludedDayMessages.tuesday,
    excludedDayMessages.wednesday,
    excludedDayMessages.thursday,
    excludedDayMessages.friday,
    excludedDayMessages.saturday,
    excludedDayMessages.sunday,
];

export default EventHandler;
